import React, { useMemo, useCallback } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateWithTime } from '../../../../utils/dateUtils';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';

const PartsDealerBrandMatchTable = ({
    data,
    totalRecords,
    isLoading,
    onPage,
    onSort,
    lazyParams,
    visibilityOptions,
    dealerTypeOptions,
    activeOnlyOptions,
    dealerFilterOptions,
    isDealerFilterLoading,
    dealerFilterValue,
    onDealerFilterSearch,
    activeFilters,
    onFiltersChange,
    onResetFilters,
    onVisibilityChange,
    onDelete,
    isUpdating,
    isDeleting
}) => {
    const { th } = useLanguage();
    const safeParams = lazyParams || { first: 0, rows: 10, sortField: null, sortOrder: null };

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const tableFilters = useMemo(
        () => ({
            dealerId: { value: activeFilters?.dealerId ?? null, matchMode: FilterMatchMode.EQUALS },
            dealerName: { value: activeFilters?.dealerName ?? null, matchMode: activeFilters?.dealerNameMatchMode ?? FilterMatchMode.CONTAINS },
            brand: { value: activeFilters?.brand ?? null, matchMode: activeFilters?.brandMatchMode ?? FilterMatchMode.CONTAINS },
            isVisible: { value: activeFilters?.isVisible ?? null, matchMode: FilterMatchMode.EQUALS },
            dealerType: { value: activeFilters?.dealerType ?? null, matchMode: FilterMatchMode.EQUALS },
            isActive: { value: activeFilters?.activeOnly ?? null, matchMode: FilterMatchMode.EQUALS },
            createdName: { value: activeFilters?.createdName ?? null, matchMode: activeFilters?.createdNameMatchMode ?? FilterMatchMode.CONTAINS }
        }),
        [activeFilters]
    );

    const handleTableFilter = useCallback(
        (e) => {
            const filters = e?.filters || {};
            const updated = { ...(activeFilters || {}) };

            if ('dealerId' in filters) updated.dealerId = filters.dealerId?.value ?? null;
            if ('dealerName' in filters) {
                updated.dealerName = filters.dealerName?.value ?? null;
                updated.dealerNameMatchMode = filters.dealerName?.matchMode ?? FilterMatchMode.CONTAINS;
            }
            if ('brand' in filters) {
                updated.brand = filters.brand?.value ?? null;
                updated.brandMatchMode = filters.brand?.matchMode ?? FilterMatchMode.CONTAINS;
            }
            if ('isVisible' in filters) updated.isVisible = filters.isVisible?.value ?? null;
            if ('dealerType' in filters) updated.dealerType = filters.dealerType?.value ?? null;
            if ('isActive' in filters) updated.activeOnly = filters.isActive?.value ?? null;
            if ('createdName' in filters) {
                updated.createdName = filters.createdName?.value ?? null;
                updated.createdNameMatchMode = filters.createdName?.matchMode ?? FilterMatchMode.CONTAINS;
            }

            onFiltersChange?.(updated);
        },
        [activeFilters, onFiltersChange]
    );

    const dealerFilterTemplate = useCallback(
        (options) => (
            <Dropdown
                value={options.value}
                options={dealerFilterOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                optionLabel="name"
                optionValue="value"
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                filter
                filterBy="name,dealerName,sapDealerId,value"
                filterValue={dealerFilterValue}
                onFilter={(e) => onDealerFilterSearch(e.filter)}
                loading={isDealerFilterLoading}
                emptyFilterMessage={th('common.noRecords')}
                style={{ minWidth: '12rem' }}
            />
        ),
        [dealerFilterOptions, dealerFilterValue, isDealerFilterLoading, onDealerFilterSearch, th]
    );

    const visibilityFilterTemplate = useCallback(
        (options) => (
            <Dropdown
                value={options.value}
                options={visibilityOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                optionLabel="label"
                optionValue="value"
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                style={{ minWidth: '10rem' }}
            />
        ),
        [visibilityOptions, th]
    );

    const dealerTypeFilterTemplate = useCallback(
        (options) => (
            <Dropdown
                value={options.value}
                options={dealerTypeOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                optionLabel="label"
                optionValue="value"
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                style={{ minWidth: '10rem' }}
            />
        ),
        [dealerTypeOptions, th]
    );

    const activeOnlyFilterTemplate = useCallback(
        (options) => (
            <Dropdown
                value={options.value}
                options={activeOnlyOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                optionLabel="label"
                optionValue="value"
                placeholder={th('common.search')}
                className="p-column-filter"
                showClear
                style={{ minWidth: '10rem' }}
            />
        ),
        [activeOnlyOptions, th]
    );

    const actionFilterTemplate = useCallback(
        () => (
            <div className="flex justify-content-center align-items-center">
                <i onClick={onResetFilters} className="pi pi-filter-slash cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1rem' }} title={th('filter.reset')}></i>
            </div>
        ),
        [onResetFilters, th]
    );

    const makeTextColumn = (field, headerKey, options = {}) => {
        const { body, sortable = true, minWidth = '10rem', filterField = field } = options;
        return {
            field,
            columnKey: field,
            header: th(headerKey),
            sortable,
            style: { minWidth },
            body,
            filter: true,
            filterField,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

    const makeDropdownColumn = (field, headerKey, filterElement, options = {}) => {
        const { body, sortable = true, minWidth = '10rem', filterField = field } = options;
        return {
            field,
            columnKey: field,
            header: th(headerKey),
            sortable,
            style: { minWidth },
            body,
            filter: true,
            filterField,
            filterElement,
            showFilterMenu: false,
            showClearButton: false
        };
    };

    const columns = useMemo(() => {
        const dealerTypeTemplate = (row) => {
            if (row.dealerType === 1) return th('partsDealerBrandMatch.domestic');
            if (row.dealerType === 2) return th('partsDealerBrandMatch.foreign');
            return '';
        };

        const statusTemplate = (row) => {
            const isActive = row.isActive === 1;
            return <span className={`p-tag ${isActive ? 'p-tag-success' : 'p-tag-danger'}`}>{isActive ? th('common.active') : th('common.inactive')}</span>;
        };

        const visibilityTemplate = (row) => {
            if (!visibilityOptions?.length) {
                return row.isVisibleText || '';
            }

            return (
                <Dropdown
                    value={row.isVisible}
                    options={visibilityOptions}
                    onChange={(e) => onVisibilityChange(row, e.value)}
                    optionLabel="label"
                    optionValue="value"
                    className="w-10rem"
                    disabled={isUpdating}
                    placeholder={row.isVisibleText || th('partsDealerBrandMatch.visibility')}
                />
            );
        };

        const actionTemplate = (row) => (
            <div className="flex gap-2 justify-content-center">
                <i onClick={() => !isDeleting && onDelete(row)} className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700" style={{ fontSize: '1.1rem' }} title={th('common.delete')}></i>
            </div>
        );

        return [
            {
                field: '__actions',
                header: th('common.actions'),
                headerLabel: th('common.actions'),
                body: actionTemplate,
                filter: true,
                filterElement: actionFilterTemplate,
                showFilterMenu: false,
                lockedFromPrefs: true,
                lockedPosition: 'start',
                style: { width: '5rem' },
                align: 'center'
            },
            makeDropdownColumn('dealerId', 'partsDealerBrandMatch.columnDealerId', dealerFilterTemplate, { minWidth: '12rem' }),
            makeTextColumn('dealerName', 'partsDealerBrandMatch.columnDealerName', { minWidth: '12rem' }),
            makeDropdownColumn('dealerType', 'partsDealerBrandMatch.columnDealerType', dealerTypeFilterTemplate, { body: dealerTypeTemplate, minWidth: '10rem' }),
            makeTextColumn('brand', 'partsDealerBrandMatch.columnBrand', { minWidth: '8rem' }),
            makeDropdownColumn('isVisible', 'partsDealerBrandMatch.columnVisibility', visibilityFilterTemplate, { body: visibilityTemplate, minWidth: '12rem' }),
            makeDropdownColumn('isActive', 'common.status', activeOnlyFilterTemplate, { body: statusTemplate, minWidth: '8rem', filterField: 'isActive' }),
            makeTextColumn('createdName', 'common.created', { minWidth: '10rem' }),
            {
                field: 'createdDate',
                columnKey: 'createdDate',
                header: th('common.createdDate'),
                sortable: true,
                style: { minWidth: '10rem' },
                body: (row) => formatDateWithTime(row?.createdDate)
            }
        ];
    }, [
        th,
        visibilityOptions,
        dealerTypeOptions,
        activeOnlyOptions,
        dealerFilterTemplate,
        visibilityFilterTemplate,
        dealerTypeFilterTemplate,
        activeOnlyFilterTemplate,
        actionFilterTemplate,
        isUpdating,
        isDeleting,
        onVisibilityChange,
        onDelete,
        textMatchModeOptions
    ]);

    return (
        <PreferencesDataTable
            tableKey="parts-dealer-brand-match"
            columns={columns}
            value={data}
            dataKey="id"
            lazy
            paginator
            first={safeParams.first}
            rows={safeParams.rows}
            totalRecords={totalRecords}
            onPage={onPage}
            loading={isLoading}
            emptyMessage={th('partsDealerBrandMatch.emptyMessage')}
            rowsPerPageOptions={[10, 25, 50, 100]}
            showGridlines
            stripedRows
            removableSort
            sortMode="single"
            sortField={safeParams.sortField}
            sortOrder={safeParams.sortOrder}
            onSort={onSort}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
        />
    );
};

export default PartsDealerBrandMatchTable;
